import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/dashboard';
import { WikiPageForm } from '../../components/wiki/WikiPageForm';
import { wikiService } from '../../services/wikiService';
import { WikiPage, UpdateWikiPageDto } from '../../types/wiki';
import { Page, PageHeader, Skeleton } from '../../components/ui';

export const WikiEditPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wikiPage, setWikiPage] = useState<WikiPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        if (!id) return;
        setLoading(true);
        const data = await wikiService.getWikiPage(id);
        setWikiPage(data);
      } catch (err) {
        setError('Failed to load wiki page');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleSubmit = async (data: UpdateWikiPageDto) => {
    if (!id) return;
    await wikiService.updateWikiPage(id, data);
    navigate(`/wiki/${id}`);
  };

  return (
    <DashboardLayout>
      <Page>
        <div className="mx-auto max-w-[768px]">
          <PageHeader
            back={{ to: id ? `/wiki/${id}` : '/wiki', label: 'Back' }}
            title="Edit page"
            meta={
              wikiPage ? (
                <span className="block max-w-full truncate">
                  {[wikiPage.entityName, `v${wikiPage.version}`].filter(Boolean).join(' · ')}
                </span>
              ) : undefined
            }
          />

          {loading ? (
            <div aria-hidden="true" className="space-y-4">
              <div>
                <Skeleton className="mb-2 h-3 w-10" />
                <Skeleton className="h-9 w-full rounded-xl" />
              </div>
              <div>
                <Skeleton className="mb-2 h-3 w-14" />
                <Skeleton className="h-[420px] w-full rounded-2xl" />
              </div>
            </div>
          ) : error ? (
            <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{error}</p>
          ) : wikiPage ? (
            <WikiPageForm
              wikiPage={wikiPage}
              entityType={wikiPage.entityType}
              entityId={wikiPage.entityId}
              onSubmit={handleSubmit}
              onCancel={() => navigate(id ? `/wiki/${id}` : '/wiki')}
            />
          ) : null}
        </div>
      </Page>
    </DashboardLayout>
  );
};
